import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { FieldArray, Formik, FormikErrors } from "formik";
import {
  AnimalGroup,
  ArableField,
  CROP_HARVEST_OPTIONS,
  CROP_UNITS,
  CROP_YIELD_TYPE,
  CropYieldAmount,
  CropYieldDetails,
  Field,
  GRASS_UNITS,
  GRASS_YIELD_TYPES,
  GrassYieldAmount,
  GrassYieldDetails,
  MEAT_UNITS,
  MeatYieldAmount,
  MeatYieldDetails,
  MILK_UNITS,
  MilkYieldAmount,
  MilkYieldDetails,
  REVENUE_CURRENCY,
  SOLD_OR_KEPT,
  useArableFields,
  useCurrentFarmAnimalGroups,
  useFields,
  YIELD_TYPES,
  YieldEvent,
} from "model";
import { MutableRefObject, ReactNode, useState } from "react";

import EnterDate from "features/general/EnterDate";
import { cloneDeep } from "lodash";
import * as Yup from "yup";
import CropTypeMissingDialog from "./CropTypeMissingDialog";
import EnterYieldEventYieldDetails from "./EnterYieldEventYieldDetails";
import YieldTypeRadioButtons from "./YieldTypeRadioButtons";

const cardSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  p: 2.5,
};

interface Props {
  yieldEvent: YieldEvent | undefined;
  handleSave: (yieldInput: YieldEvent) => void;
  dirtyRef: MutableRefObject<boolean>;
  children: (args: {
    steps: { content: ReactNode }[];
    submitForm: () => Promise<void>;
  }) => ReactNode;
}

export type YieldDetailsFormData = {
  amount?: number;
  soldOrKept?: SOLD_OR_KEPT;
  detailYieldType?:
    | GRASS_YIELD_TYPES
    | CROP_YIELD_TYPE
    | YIELD_TYPES.MEAT
    | YIELD_TYPES.MILK;
  unitType?: MILK_UNITS | MEAT_UNITS | GRASS_UNITS | CROP_UNITS;
  revenue?: number;
};
export type YieldEventFormData = {
  note?: string;
  yieldEventDate: Date;
  yieldType?: YIELD_TYPES;
  fieldOrAnimalGroupGuids: string[];
  yieldAmounts: YieldDetailsFormData[];
};

const convertToFormInput = (yieldEvent: YieldEvent): YieldEventFormData => {
  return {
    note: yieldEvent.note,
    yieldEventDate: new Date(yieldEvent.yieldEventDate),
    yieldType: yieldEvent.yieldDetails.yieldType,
    fieldOrAnimalGroupGuids:
      yieldEvent.yieldDetails.yieldType === YIELD_TYPES.GRASS ||
      yieldEvent.yieldDetails.yieldType === YIELD_TYPES.CROP
        ? yieldEvent.yieldDetails.fieldGuids
        : yieldEvent.yieldDetails.animalGroupGuids,
    yieldAmounts: yieldEvent.yieldDetails.yieldAmounts.map((yieldDetail) => {
      let detailYieldType:
        | GRASS_YIELD_TYPES
        | CROP_YIELD_TYPE
        | YIELD_TYPES.MEAT
        | YIELD_TYPES.MILK;
      if (yieldEvent.yieldDetails.yieldType === YIELD_TYPES.GRASS) {
        detailYieldType = (yieldDetail as GrassYieldAmount).grassYieldType;
      } else if (yieldEvent.yieldDetails.yieldType === YIELD_TYPES.CROP) {
        detailYieldType = (yieldDetail as CropYieldAmount).cropYieldType;
      } else {
        detailYieldType = yieldEvent.yieldDetails.yieldType;
      }

      return {
        amount: yieldDetail.amount,
        soldOrKept: yieldDetail.soldOrKept,
        detailYieldType,
        unitType: yieldDetail.unitType,
        revenue: yieldDetail.revenue,
      };
    }),
  };
};

const getYieldAmounts = (
  yieldEventFormData: YieldEventFormData
):
  | GrassYieldAmount[]
  | MeatYieldAmount[]
  | MilkYieldAmount[]
  | CropYieldAmount[] => {
  const resultArray = yieldEventFormData.yieldAmounts.map(
    (yieldDetails: YieldDetailsFormData) => {
      const revenue =
        yieldDetails.soldOrKept === SOLD_OR_KEPT.KEPT
          ? undefined
          : (yieldDetails.revenue as unknown as string) === ""
          ? undefined
          : yieldDetails.revenue;
      const result: { [key: string]: string | number | undefined } = {};
      result["unitType"] = yieldDetails.unitType;
      result["amount"] = yieldDetails.amount;
      result["soldOrKept"] = yieldDetails.soldOrKept;
      result["revenue"] = revenue;
      result["revenueCurrency"] = revenue ? REVENUE_CURRENCY.GBP : undefined;
      if (yieldEventFormData.yieldType === YIELD_TYPES.GRASS) {
        result["grassYieldType"] = yieldDetails.detailYieldType;
      }
      if (yieldEventFormData.yieldType === YIELD_TYPES.CROP) {
        result["cropYieldType"] = yieldDetails.detailYieldType;
      }
      return result;
    }
  );
  if (yieldEventFormData.yieldType === YIELD_TYPES.GRASS) {
    return resultArray as GrassYieldAmount[];
  }
  if (yieldEventFormData.yieldType === YIELD_TYPES.CROP) {
    return resultArray as CropYieldAmount[];
  }
  if (yieldEventFormData.yieldType === YIELD_TYPES.MILK) {
    return resultArray as MilkYieldAmount[];
  }
  return resultArray as MeatYieldAmount[];
};

const getYieldDetails = (
  yieldEventFormData: YieldEventFormData
):
  | GrassYieldDetails
  | MilkYieldDetails
  | MeatYieldDetails
  | CropYieldDetails => {
  if (yieldEventFormData.yieldType === YIELD_TYPES.GRASS) {
    return {
      yieldType: YIELD_TYPES.GRASS,
      yieldAmounts: getYieldAmounts(yieldEventFormData) as GrassYieldAmount[],
      fieldGuids: yieldEventFormData.fieldOrAnimalGroupGuids,
    };
  }
  if (yieldEventFormData.yieldType === YIELD_TYPES.CROP) {
    return {
      yieldType: YIELD_TYPES.CROP,
      yieldAmounts: getYieldAmounts(yieldEventFormData) as CropYieldAmount[],
      fieldGuids: yieldEventFormData.fieldOrAnimalGroupGuids,
    };
  }
  if (yieldEventFormData.yieldType === YIELD_TYPES.MEAT) {
    return {
      yieldType: YIELD_TYPES.MEAT,
      yieldAmounts: getYieldAmounts(yieldEventFormData) as MeatYieldAmount[],
      animalGroupGuids: yieldEventFormData.fieldOrAnimalGroupGuids,
    };
  }
  return {
    yieldType: YIELD_TYPES.MILK,
    yieldAmounts: getYieldAmounts(yieldEventFormData) as MilkYieldAmount[],
    animalGroupGuids: yieldEventFormData.fieldOrAnimalGroupGuids,
  };
};

const convertFromFormInput = (
  yieldEventFormData: YieldEventFormData
): YieldEvent => {
  return {
    note: yieldEventFormData.note,
    yieldDetails: getYieldDetails(yieldEventFormData),
    yieldEventDate: new Date(yieldEventFormData.yieldEventDate).toISOString(),
    guid: " ",
  };
};

export default function EnterYieldEvent({
  yieldEvent,
  handleSave,
  dirtyRef,
  children,
}: Props) {
  const { t } = useTranslation();

  const { data: allAnimalGroups, isLoading: loading } =
    useCurrentFarmAnimalGroups();

  const { data: allGrassFields = [] } = useFields();
  const { data: allArableFields = [] } = useArableFields();

  const [missingCropTypesDialogOpen, setMissingCropTypesDialogOpen] =
    useState(false);

  const checkCropMissing = (
    arableFields: ArableField[],
    crops: YieldDetailsFormData[] | undefined
  ) => {
    if (!crops) {
      return false;
    }
    const cropTypes = crops?.map((crop) => crop.detailYieldType);
    for (const arableField of arableFields) {
      // A field is covered if the yield list contains any harvest the crop can
      // produce (e.g. a maize field is satisfied by maize silage or grain).
      const allowedHarvests = CROP_HARVEST_OPTIONS[arableField.arableType] ?? [
        arableField.arableType,
      ];
      const covered = allowedHarvests.some((harvest) =>
        cropTypes.includes(harvest)
      );
      if (!covered) {
        setMissingCropTypesDialogOpen(true);
        return false;
      }
    }
    return true;
  };

  const validNumber = (min = 0) =>
    Yup.number()
      .typeError(t("edit.minNumber.error", { min }))
      .moreThan(min, t("edit.minNumber.error", { min }))
      .required(t("edit.required.error"));

  const YieldEventSchema = Yup.object()
    .shape({
      note: Yup.string().optional(),
      yieldEventDate: Yup.date(),
      yieldType: Yup.mixed<YIELD_TYPES>()
        .oneOf(Object.values(YIELD_TYPES))
        .required(),
      fieldOrAnimalGroupGuids: Yup.array().of(Yup.string()).required().min(1),
      yieldAmounts: Yup.array()
        .of(
          Yup.object().shape({
            amount: validNumber(0).required(),
            soldOrKept: Yup.mixed<SOLD_OR_KEPT>()
              .oneOf(Object.values(SOLD_OR_KEPT))
              .required(),
            detailYieldType: Yup.mixed<
              | GRASS_YIELD_TYPES
              | CROP_YIELD_TYPE
              | YIELD_TYPES.MEAT
              | YIELD_TYPES.MILK
            >()
              .oneOf([
                ...Object.values(GRASS_YIELD_TYPES),
                ...Object.values(CROP_YIELD_TYPE),
                YIELD_TYPES.MEAT,
                YIELD_TYPES.MILK,
              ])
              .required(),
            unitType: Yup.mixed<
              MILK_UNITS | MEAT_UNITS | GRASS_UNITS | CROP_UNITS
            >()
              .oneOf([
                ...Object.values(MILK_UNITS),
                ...Object.values(MEAT_UNITS),
                ...Object.values(GRASS_UNITS),
                ...Object.values(CROP_UNITS),
              ])
              .required(),
            revenue: validNumber(0).optional(),
          })
        )
        .required()
        .min(1),
    })
    .test(
      "crops-missing",
      "Crops missing in crop list",
      (yieldEventFormData) => {
        const selectedArableFields = allArableFields.filter(
          (arableField: ArableField) =>
            yieldEventFormData?.fieldOrAnimalGroupGuids.includes(
              arableField.guid
            )
        );
        return checkCropMissing(
          selectedArableFields,
          yieldEventFormData?.yieldAmounts
        );
      }
    );

  const getPossibleCropYields = (arableFieldGuids: string[]) => {
    const selectedArableFields = allArableFields.filter(
      (arableField: ArableField) => arableFieldGuids.includes(arableField.guid)
    );

    // Each crop maps to the harvest types it can produce (grain, silage,
    // straw, …) via CROP_HARVEST_OPTIONS — see model/cropHarvestFeed.ts.
    const harvestTypes = new Set<CROP_YIELD_TYPE>();
    for (const arableField of selectedArableFields) {
      const options = CROP_HARVEST_OPTIONS[arableField.arableType] ?? [];
      for (const option of options) {
        harvestTypes.add(option);
      }
    }

    const result: { [key: string]: string } = {};
    for (const crop of harvestTypes) {
      result[crop] = CROP_YIELD_TYPE[crop];
    }
    return result;
  };

  const newYieldAmount = {
    unitType: "" as GRASS_UNITS,
    amount: "" as unknown as number,
    soldOrKept: "" as SOLD_OR_KEPT,
    revenue: "" as unknown as number,
    detailYieldType: "" as
      | GRASS_YIELD_TYPES
      | CROP_YIELD_TYPE
      | YIELD_TYPES.MILK
      | YIELD_TYPES.MEAT,
  };

  if (loading) {
    return null;
  }

  const initialYieldEvent = yieldEvent
    ? convertToFormInput(yieldEvent)
    : {
        note: "",
        yieldEventDate: new Date(),
        yieldType: YIELD_TYPES.GRASS,
        yieldAmounts: [newYieldAmount],
        fieldOrAnimalGroupGuids: [],
      };

  const getSelectionItems = (yieldType?: YIELD_TYPES) => {
    if (yieldType === YIELD_TYPES.MEAT || yieldType === YIELD_TYPES.MILK) {
      return allAnimalGroups ?? [];
    }
    if (yieldType === YIELD_TYPES.CROP) {
      return allArableFields;
    }
    return allGrassFields;
  };

  const getSelectionLabel = (yieldType?: YIELD_TYPES) => {
    if (yieldType === YIELD_TYPES.MEAT || yieldType === YIELD_TYPES.MILK) {
      return t("yieldEventInput.animalGroups.text");
    }
    if (yieldType === YIELD_TYPES.CROP) {
      return t("yieldEventInput.arableFields.text");
    }
    return t("yieldEventInput.grassFields.text");
  };

  return (
    <Formik
      enableReinitialize={false}
      initialValues={initialYieldEvent}
      validationSchema={YieldEventSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(yieldEventFormData: YieldEventFormData) => {
        const selectedArableFields = allArableFields.filter(
          (arableField: ArableField) =>
            yieldEventFormData?.fieldOrAnimalGroupGuids.includes(
              arableField.guid
            )
        );
        const enoughFeedAvailable = checkCropMissing(
          selectedArableFields,
          yieldEventFormData?.yieldAmounts
        );
        if (!enoughFeedAvailable) {
          return;
        } else {
          void handleSave({
            ...convertFromFormInput(yieldEventFormData),
            guid: yieldEvent?.guid ?? "",
          });
        }
      }}
    >
      {({
        values,
        handleChange,
        errors,
        validateField,
        setFieldValue,
        resetForm,
        dirty,
        submitForm,
      }) => {
        dirtyRef.current = dirty;
        const handleInputChange = async (
          e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          fieldName: string
        ) => {
          await (handleChange(e) as unknown as Promise<void>);
          await (validateField(fieldName) as unknown as Promise<void>);
        };

        const handleSelectionToggle = async (guid: string) => {
          const isSelected = (values.fieldOrAnimalGroupGuids ?? []).includes(
            guid
          );
          const updated = isSelected
            ? values.fieldOrAnimalGroupGuids.filter((g: string) => g !== guid)
            : [...values.fieldOrAnimalGroupGuids, guid];
          await setFieldValue("fieldOrAnimalGroupGuids", updated);
        };

        const selectionItems = getSelectionItems(values.yieldType);
        const selectionLabel = getSelectionLabel(values.yieldType);

        const steps = [
          {
            content: (
              <Container style={{ textAlign: "left" }}>
                <Grid container spacing={2.5}>
                  {/* Date & Yield Type */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("yield.date.type.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <EnterDate
                            disabled={false}
                            date={values.yieldEventDate}
                            setDate={async (date: Date | null) => {
                              await setFieldValue(
                                "yieldEventDate",
                                date,
                                false
                              );
                            }}
                            dateError={!!errors.yieldEventDate}
                            name={"yieldEventDate"}
                          />
                        </Grid>
                        <Grid
                          size={{ xs: 12, sm: 8 }}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <YieldTypeRadioButtons
                            disabled={false}
                            onChange={async (event) => {
                              await handleInputChange(event, `yieldType`);
                            }}
                            currentValue={values.yieldType}
                            inputError={!!errors.yieldType}
                            name={`yieldType`}
                            resetForm={resetForm}
                            setFieldValue={setFieldValue}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Field / Animal Group selection checkbox list */}
                  <Grid size={12}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: errors.fieldOrAnimalGroupGuids
                          ? "error.main"
                          : undefined,
                        fontWeight: 500,
                      }}
                    >
                      {selectionLabel}
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                        },
                        borderRadius: "8px",
                        border: "1px solid",
                        borderColor: errors.fieldOrAnimalGroupGuids
                          ? "error.main"
                          : "rgba(0, 0, 0, 0.12)",
                        backgroundColor: "white",
                        maxHeight: 220,
                        overflowY: "auto",
                        py: 0.5,
                        px: 1,
                      }}
                    >
                      {[...selectionItems]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((item: Field | ArableField | AnimalGroup) => {
                          const isSelected = (
                            values.fieldOrAnimalGroupGuids ?? []
                          ).includes(item.guid);
                          return (
                            <FormControlLabel
                              key={item.guid}
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() =>
                                    handleSelectionToggle(item.guid)
                                  }
                                  size="small"
                                  sx={{
                                    color: "rgba(0,0,0,0.3)",
                                    "&.Mui-checked": {
                                      color: "#7EC254",
                                    },
                                  }}
                                />
                              }
                              label={item.name}
                              sx={{
                                mx: 0,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.875rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                },
                              }}
                            />
                          );
                        })}
                    </Box>
                  </Grid>
                  {/* Yield details */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("yield.details.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <FieldArray name="yieldAmounts">
                        {({ remove, push }) => {
                          return (
                            <>
                              {values.yieldAmounts.length > 0 &&
                                values.yieldAmounts.map(
                                  (yieldDetail, index) => {
                                    let possibleYieldTypeValues: {
                                      [key: string]: string;
                                    } = {};
                                    let yieldTypeTPrefix = "";
                                    let possibleUnitTypeValues = undefined;
                                    let unitTypeTPrefix = "";
                                    if (
                                      values.yieldType === YIELD_TYPES.GRASS
                                    ) {
                                      possibleYieldTypeValues =
                                        GRASS_YIELD_TYPES;
                                      yieldTypeTPrefix =
                                        "yieldEvent.yieldType.option";
                                      possibleUnitTypeValues = GRASS_UNITS;
                                      unitTypeTPrefix =
                                        "yieldEvent.unitType.option";
                                    } else if (
                                      values.yieldType === YIELD_TYPES.CROP
                                    ) {
                                      possibleYieldTypeValues =
                                        getPossibleCropYields(
                                          values.fieldOrAnimalGroupGuids
                                        );
                                      yieldTypeTPrefix =
                                        "yieldEvent.yieldType.option";
                                      possibleUnitTypeValues = CROP_UNITS;
                                      unitTypeTPrefix =
                                        "yieldEvent.unitType.option";
                                    } else if (
                                      values.yieldType === YIELD_TYPES.MEAT
                                    ) {
                                      possibleYieldTypeValues[
                                        YIELD_TYPES.MEAT
                                      ] = YIELD_TYPES[YIELD_TYPES.MEAT];
                                      yieldTypeTPrefix = "yieldEvent.yieldType";
                                      possibleUnitTypeValues = MEAT_UNITS;
                                      unitTypeTPrefix =
                                        "yieldEvent.unitType.option";
                                    } else {
                                      possibleYieldTypeValues[
                                        YIELD_TYPES.MILK
                                      ] = YIELD_TYPES[YIELD_TYPES.MILK];
                                      yieldTypeTPrefix = "yieldEvent.yieldType";
                                      possibleUnitTypeValues = MILK_UNITS;
                                      unitTypeTPrefix =
                                        "yieldEvent.unitType.option";
                                    }

                                    return (
                                      <EnterYieldEventYieldDetails
                                        key={index}
                                        handleInputChange={handleInputChange}
                                        index={index}
                                        errors={
                                          errors?.yieldAmounts?.[index] as
                                            | FormikErrors<YieldDetailsFormData>
                                            | undefined
                                        }
                                        yieldDetail={yieldDetail}
                                        remove={() => remove(index)}
                                        possibleYieldTypeValues={
                                          possibleYieldTypeValues
                                        }
                                        yieldTypeTPrefix={yieldTypeTPrefix}
                                        possibleUnitTypeValues={
                                          possibleUnitTypeValues
                                        }
                                        unitTypeTPrefix={unitTypeTPrefix}
                                        setFieldValue={setFieldValue}
                                      />
                                    );
                                  }
                                )}
                              <Button
                                onClick={() => {
                                  const newValues = cloneDeep(newYieldAmount);
                                  if (values.yieldType === YIELD_TYPES.MEAT) {
                                    newValues.detailYieldType =
                                      YIELD_TYPES.MEAT;
                                  }
                                  if (values.yieldType === YIELD_TYPES.MILK) {
                                    newValues.detailYieldType =
                                      YIELD_TYPES.MILK;
                                  }
                                  push(newValues);
                                }}
                                sx={{
                                  color: "#5E5E5E",
                                  textTransform: "none",
                                  mt: 1,
                                }}
                              >
                                + {t("yieldEventInput.addYield")}
                              </Button>
                            </>
                          );
                        }}
                      </FieldArray>
                    </Box>
                  </Grid>

                  {/* Additional details (optional) */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("common.additionalDetails.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <TextField
                            id="yield-event-input-note"
                            label={t("reseedEvent.notes.label")}
                            value={values.note}
                            helperText={errors.note}
                            error={!!errors.note}
                            fullWidth
                            onChange={(e) => handleInputChange(e, "note")}
                            name={"note"}
                            multiline
                            maxRows={12}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>

                <CropTypeMissingDialog
                  arableFields={allArableFields.filter(
                    (arableField: ArableField) =>
                      values?.fieldOrAnimalGroupGuids.includes(arableField.guid)
                  )}
                  crops={values.yieldAmounts}
                  open={missingCropTypesDialogOpen}
                  setClose={() => setMissingCropTypesDialogOpen(false)}
                />
              </Container>
            ),
          },
        ];

        return <>{children({ steps, submitForm })}</>;
      }}
    </Formik>
  );
}
